import type { SystemId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import { isPrivacyClass } from './privacy.js';
import type { DomainGroup, Maturity, Platform, PlatformAvailability } from './taxonomy.js';
import { PLATFORMS, isDomainGroup, isMaturity } from './taxonomy.js';

/** A system's surface: either a route path or a non-route surface name. */
export type SystemSurface =
  | { readonly kind: 'route'; readonly path: string }
  | { readonly kind: 'surface'; readonly name: string };

export type SourcePreservation = 'none' | 'preserve-before-promote';

/** Directory metadata for every system in Life OS (powers All Systems + Search). */
export interface SystemMetadata {
  readonly id: SystemId;
  readonly label: string;
  readonly shortPurpose: string;
  readonly group: DomainGroup;
  readonly maturity: Maturity;
  readonly surface: SystemSurface;
  readonly privacyClass: PrivacyClass;
  /** Availability declared for EVERY platform; no platform omitted. */
  readonly platformAvailability: Readonly<Record<Platform, PlatformAvailability>>;
  readonly pinnedEligible: boolean;
  readonly recentEligible: boolean;
  readonly adminOrArchive: boolean;
  readonly sourcePreservation?: SourcePreservation;
}

export interface MetadataProblem {
  readonly systemId: string;
  readonly rule: string;
  readonly detail: string;
}

/**
 * Validate a single system's metadata. Returns problems (empty = valid).
 * Mirrors the feature-registry / parity doctor rules so code and CI agree.
 */
export function validateSystemMetadata(meta: SystemMetadata): MetadataProblem[] {
  const problems: MetadataProblem[] = [];
  const id = String(meta.id);
  const push = (rule: string, detail: string) => problems.push({ systemId: id, rule, detail });

  if (!meta.id || id.trim() === '') push('id-present', 'system id is required');
  if (!meta.label || meta.label.trim() === '') push('label-present', 'label is required');
  if (!meta.shortPurpose || meta.shortPurpose.trim() === '') {
    push('purpose-present', 'shortPurpose is required');
  }
  if (!isMaturity(meta.maturity)) push('maturity-valid', `invalid maturity: ${meta.maturity}`);
  if (!isDomainGroup(meta.group)) push('group-valid', `invalid group: ${meta.group}`);
  if (!isPrivacyClass(meta.privacyClass)) {
    push('privacy-present', `invalid privacyClass: ${meta.privacyClass}`);
  }

  // Every platform key must be present.
  for (const p of PLATFORMS) {
    if (meta.platformAvailability[p] === undefined) {
      push('platform-complete', `missing platform availability for "${p}"`);
    }
  }

  // daily/supported systems must not be not_applicable on a core platform
  // without an explicit, reviewable exception.
  if (meta.maturity === 'daily' || meta.maturity === 'supported') {
    for (const p of PLATFORMS) {
      if (meta.platformAvailability[p] === 'not_applicable') {
        push(
          'parity-no-silent-drop',
          `${meta.maturity} system marks "${p}" not_applicable; use "excepted" with review`,
        );
      }
    }
  }

  return problems;
}
